import axios from 'axios';

// Interface for metadata with social links
export interface TokenMetadata {
  name?: string;
  symbol?: string;
  description?: string;
  image?: string;
  twitter?: string;
  telegram?: string;
  website?: string;
  discord?: string;
  youtube?: string;
  properties?: {
    [key: string]: any;
  };
  attributes?: Array<{
    trait_type: string;
    value: string;
  }>;
  [key: string]: any; // Allow any other properties
}

// Cache to store fetched metadata
const metadataCache = new Map<string, TokenMetadata>();
const loadingURIs = new Map<string, Promise<TokenMetadata>>();
const failedURIs = new Set<string>();

// Allow setting a timeout for fetch operations
const FETCH_TIMEOUT = 8000; // 8 seconds

/**
 * Fetches metadata from a token's URI with caching
 */
export const fetchTokenMetadata = async (uri: string | null | undefined): Promise<TokenMetadata | null> => {
  // Handle invalid URIs
  if (!uri) {
    return null;
  }

  // Normalize the URI
  const normalizedUri = uri.trim();

  // Check if this is a blacklisted domain - skip entirely
  if (isBlacklistedDomain(normalizedUri)) {
    return null;
  }

  // Check cache first
  if (metadataCache.has(normalizedUri)) {
    return metadataCache.get(normalizedUri) || null;
  }

  // Check if URI is in the failed list
  if (failedURIs.has(normalizedUri)) {
    return null;
  }

  // Check if this URI is already being loaded
  if (loadingURIs.has(normalizedUri)) {
    return loadingURIs.get(normalizedUri) || null;
  }

  // Create a fetch promise
  const fetchPromise = new Promise<TokenMetadata>(async (resolve, reject) => {
    // Set a timeout
    const timeoutId = setTimeout(() => {
      failedURIs.add(normalizedUri);
      loadingURIs.delete(normalizedUri);
      reject(new Error(`Timeout fetching metadata from URI: ${normalizedUri}`));
    }, FETCH_TIMEOUT);

    try {
      // Handle IPFS URIs by converting to HTTP gateway URL
      let fetchUri = normalizedUri;
      if (fetchUri.startsWith('ipfs://')) {
        fetchUri = `https://ipfs.io/ipfs/${fetchUri.replace('ipfs://', '')}`;
      }

      // Only use CORS proxy for specific known-working domains
      if (needsCorsProxy(fetchUri)) {
        fetchUri = `https://api.allorigins.win/get?url=${encodeURIComponent(fetchUri)}`;
      }

      // Fetch with axios
      const response = await axios.get(fetchUri, {
        timeout: FETCH_TIMEOUT,
        headers: {
          'Accept': 'application/json'
        }
      });

      // Process the data (handle allorigins wrapper)
      let metadata: TokenMetadata;
      if (needsCorsProxy(normalizedUri) && response.data?.contents) {
        // Parse the wrapped response from allorigins
        metadata = JSON.parse(response.data.contents);
      } else {
        metadata = response.data || {};
      }

      // Clean up metadata format
      metadata = processMetadata(metadata);

      // Store in cache
      metadataCache.set(normalizedUri, metadata);

      // Clear timeout
      clearTimeout(timeoutId);

      // Resolve with the metadata
      resolve(metadata);
    } catch (error: any) {
      // Simplified error handling - just mark as failed and move on
      failedURIs.add(normalizedUri);
      clearTimeout(timeoutId);
      reject(error);
    } finally {
      // Remove from loading list
      loadingURIs.delete(normalizedUri);
    }
  });

  // Add to loading list
  loadingURIs.set(normalizedUri, fetchPromise);

  try {
    return await fetchPromise;
  } catch (error) {
    // Silently fail for metadata - it's not critical
    return null;
  }
};

/**
 * Determine if a URL should be completely skipped (blacklisted domains)
 */
const isBlacklistedDomain = (url: string): boolean => {
  const blacklistedDomains = [
    'metadata.boop.fun',  // Returns 403 with CORS proxy
    '66.135.23.176',     // IP-based metadata servers with CORS issues
    '149.51.224.102',    // IP-based metadata servers with CORS issues
    'corsproxy.io',      // Don't proxy the proxy
    // Add more problematic domains as needed
  ];

  // Also block any IP address-based URLs (they rarely work with CORS)
  const ipPattern = /^https?:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/;
  if (ipPattern.test(url)) {
    return true;
  }

  // Block URLs that look like temporary/problematic metadata services
  const problematicPatterns = [
    '/json/',       // Pattern seen in failing URLs
    ':3010',        // Specific ports often used by temporary services
    ':3012',
  ];

  if (problematicPatterns.some(pattern => url.includes(pattern))) {
    return true;
  }

  return blacklistedDomains.some(domain => url.includes(domain));
};

/**
 * Determine if a URL likely needs a CORS proxy (reduced list)
 */
const needsCorsProxy = (url: string): boolean => {
  // Only use CORS proxy for domains we know work with it
  const corsProblematicDomains = [
    'arweave.net',
    'ipfs.dweb.link',
    'gateway.pinata.cloud'
  ];

  return corsProblematicDomains.some(domain => url.includes(domain));
};

/**
 * Process raw metadata to normalize format
 */
const processMetadata = (metadata: any): TokenMetadata => {
  const processed: TokenMetadata = { ...metadata };

  // Extract social links from properties if they exist
  if (metadata.properties) {
    // Common social link properties
    const socialProperties = ['twitter', 'telegram', 'website', 'discord', 'youtube'];

    for (const prop of socialProperties) {
      if (metadata.properties[prop] && !processed[prop]) {
        processed[prop] = metadata.properties[prop];
      }
    }
  }

  // Look for links in attributes
  if (metadata.attributes && Array.isArray(metadata.attributes)) {
    for (const attr of metadata.attributes) {
      if (attr.trait_type?.toLowerCase() === 'twitter' && attr.value) {
        processed.twitter = attr.value;
      }
      if (attr.trait_type?.toLowerCase() === 'telegram' && attr.value) {
        processed.telegram = attr.value;
      }
      if (attr.trait_type?.toLowerCase() === 'website' && attr.value) {
        processed.website = attr.value;
      }
      if (attr.trait_type?.toLowerCase() === 'discord' && attr.value) {
        processed.discord = attr.value;
      }
    }
  }

  // Normalize links
  if (processed.twitter && typeof processed.twitter === 'string') {
    processed.twitter = normalizeTwitterLink(processed.twitter);
  }

  if (processed.telegram && typeof processed.telegram === 'string') {
    processed.telegram = normalizeTelegramLink(processed.telegram);
  }

  // Ensure the image URL is properly formed
  if (processed.image && typeof processed.image === 'string') {
    if (processed.image.startsWith('ipfs://')) {
      processed.image = `https://ipfs.io/ipfs/${processed.image.replace('ipfs://', '')}`;
    }
  }

  return processed;
};

/**
 * Normalize a Twitter URL
 */
const normalizeTwitterLink = (link: string): string => {
  // Handle Twitter profile links
  if (link.startsWith('@')) {
    return `https://x.com/${link.replace('@', '')}`;
  }

  // If it doesn't start with http, assume it's a username
  if (!link.startsWith('http')) {
    return `https://x.com/${link}`;
  }

  return link;
};

/**
 * Normalize a Telegram link
 */
const normalizeTelegramLink = (link: string): string => {
  // If it's just a username, convert to t.me link
  if (link.startsWith('@') || (!link.includes('.') && !link.includes('/'))) {
    return `https://t.me/${link.replace('@', '')}`;
  }

  // If it doesn't start with http, add https
  if (!link.startsWith('http')) {
    return `https://${link}`;
  }

  return link;
};

// Export a singleton service
export const metadataService = {
  fetchTokenMetadata,
  clearCache: () => {
    metadataCache.clear();
    failedURIs.clear();
  }
};

export default metadataService; 